import { useEffect, useRef, useState, type ReactNode, type CSSProperties } from "react";
import type { GridViewerHandle } from "../viewer/GridViewer";
import type { GridSelection } from "../lib/grid-selection";
import type { SelectionMode } from "../viewer/rect-selection";
import type { ViewerContextState } from "../viewer/context-state";
import type { ZoneRole } from "../lib/zone-manifest";
import GridViewer from "../viewer/GridViewer";
import { CoordinateHUD } from "./CoordinateHUD";
import { GridControls } from "./GridControls";
import { ContextModeToggle } from "./ContextModeToggle";
import { ShowcaseIntro } from "./ShowcaseIntro";
import { GridLoadingOverlay } from "./GridLoadingOverlay";
import { SectionOverline } from "./NarrativePage";
import { ZoneLegend } from "./ZoneLegend";
import { ZoneUnavailableNotice } from "./ZoneUnavailableNotice";
import { useMediaQuery } from "./media";
import { cn } from "./cn";
import { TOOLBAR_SCROLL, revealFocused } from "./toolbar-scroll";
import { artworkApi } from "../lib/artwork-api";
import { placementEditStore } from "./artwork/placementEditStore";
import { ARTWORK_DESELECT_EVENT } from "../viewer/GridArtworkLayer";
import { ZoomDiscoveryBadge } from "./grid/ZoomDiscoveryBadge";
import { useI18n } from "../i18n";

// #15 — faceted button chrome shared with the Instrument bar (Select/Inspect).
const FACET_CLIP =
  "polygon(6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px), 0 6px)";
const FACET_CLASS =
  "relative inline-flex items-center gap-1.5 px-2.5 py-1 whitespace-nowrap shrink-0 transition-colors duration-150 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--spec-cyan)] font-mono text-[10px] uppercase tracking-[0.14em] select-none";
function facetStyle(active: boolean): CSSProperties {
  return {
    clipPath: FACET_CLIP,
    background: active
      ? "linear-gradient(180deg, rgba(45, 182, 240,0.18) 0%, rgba(45, 182, 240,0.08) 100%)"
      : "rgba(255,255,255,0.02)",
    border: `1px solid ${active ? "rgba(45, 182, 240,0.55)" : "var(--spec-hairline)"}`,
    color: active ? "var(--spec-cyan)" : "var(--text-secondary)",
  };
}

const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform ?? "");

export interface GridWorkspaceProps {
  /** Selection mode passed to the shared GridViewer. */
  mode: SelectionMode;
  /** Change handler for the selection mode toggle. */
  onModeChange?: (m: SelectionMode) => void;
  /** Fires when the user commits a rectangle selection. */
  onCommitSelection?: (sel: GridSelection) => void;
  /** WO-VRD-31 — fires when a select-mode click lands outside the grid. */
  onClearSelection?: () => void;
  /**
   * H2.3 WO#3a — Forwarded viewer context state callback. The workspace
   * is a pure passthrough here: authority for the context state lives
   * in the viewer, subscription lives in the shell (`App.tsx`).
   */
  onContextStateChange?: (state: ViewerContextState) => void;
  /**
   * WO#4a — canonical zone overlay. Passed through to GridViewer.
   */
  zoneOverlay?: import("../lib/zone-manifest").ZoneManifest | null;
  /**
   * True once the zone manifest fetch has settled (ready OR unavailable) —
   * lets the loading gate know the zone overlay is no longer pending, so the
   * grid can reveal parcels + boulevards/dots together in one pass.
   */
  zoneSettled?: boolean;
  /** Defect C — the zone manifest failed to load (dots/RESERVE/Reveal depend on it). */
  zonesUnavailable?: boolean;
  /** Defect C — immediate manual retry of the zone manifest fetch. */
  onRetryZones?: () => void;
  /**
   * HOTFIX purchase-indexed-grid-refresh — monotonic nonce forwarded to the
   * GridViewer. Bumped by the shell after a purchase is authoritatively
   * indexed so the viewer reloads parcel tiles automatically.
   */
  refreshSignal?: number;
  /**
   * Macro #21 §5 — forwarded canonical occupancy readout (passthrough to
   * GridViewer). The shell subscribes for the fail-fast purchase guard.
   */
  onOccupancyChange?: (parcels: import("../lib/selection-occupancy").OccupancyRect[]) => void;
  /**
   * Optional slot for overlays that must render ABOVE the viewer canvas
   * but BELOW the HUD/controls (e.g. future ZoneOverlay, ParcelOverlay,
   * ContentOverlay per WO#2 §18). Consumers of the workspace can pass
   * any React node here.
   */
  overlaySlot?: ReactNode;
  /** MACRO #31 — hide the mobile corner HUD/controls while the Territory
   * BottomSheet is open, so the sheet is the sole focus (no overlap). */
  hideMobileChrome?: boolean;
}

/**
 * GridWorkspace — dominant Grid surface (WO#2 §7, §8, §9).
 *
 * WO#2 changes over WO#1:
 *   - GridViewer is now DIRECTLY mounted here (no more `viewer` slot),
 *     because the workspace owns the viewerRef + HUD wiring.
 *   - `chrome="external"` is passed → GridViewer's legacy internal
 *     header (THE GRID · stats · Select · Fit Grid) is suppressed; the
 *     Obsidian shell (TopNavigation + HUD + Controls) owns visible
 *     chrome.
 *   - Clean viewer API replaces the WO#1 synthetic wheel bridge.
 *   - HUD receives live world-space X/Y + zoom via viewer callbacks.
 */
export function GridWorkspace({
  mode,
  onModeChange,
  onCommitSelection,
  onClearSelection,
  onContextStateChange,
  zoneOverlay,
  zoneSettled = true,
  zonesUnavailable = false,
  onRetryZones,
  overlaySlot,
  refreshSignal,
  onOccupancyChange,
  hideMobileChrome = false,
}: GridWorkspaceProps) {
  const viewerRef = useRef<GridViewerHandle | null>(null);
  const { t } = useI18n();
  // Loading gate — reveal the grid only once parcels are painted AND the zone
  // overlay has settled, so nothing "pops in" piecemeal. A safety timeout
  // guarantees the screen never hangs if a data source stalls.
  const [parcelsReady, setParcelsReady] = useState(false);
  const [forceReady, setForceReady] = useState(false);
  const [holdDone, setHoldDone] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setForceReady(true), 9000);
    return () => window.clearTimeout(timer);
  }, []);
  const dataReady = parcelsReady && zoneSettled;
  // Claudio — keep the loading screen up ~3s longer AFTER the data is ready
  // so the reveal doesn't feel rushed.
  useEffect(() => {
    if (!dataReady) return;
    const timer = window.setTimeout(() => setHoldDone(true), 3000);
    return () => window.clearTimeout(timer);
  }, [dataReady]);
  const gridReady = forceReady || (dataReady && holdDone);
  const [hoverWorld, setHoverWorld] =
    useState<{ x: number; y: number } | null>(null);
  const [zoomLevel, setZoomLevel] = useState<number | null>(null);
  const [statusLevel, setStatusLevel] =
    useState<"live" | "cached" | "chain">("live");
  // WO-VRD-33 — "Reveal Areas" fluorescent zone-taxonomy toggle.
  const [revealZones, setRevealZones] = useState(false);
  // #15 Show & Drag — keeps owned artwork visible + grabbable at overview zoom.
  const [showDragArt, setShowDragArt] = useState(false);
  // WO-VRD-33 — hovered legend category (isolates one zone on the grid).
  const [zoneHoverRole, setZoneHoverRole] = useState<ZoneRole | null>(null);
  // WO-VRD-34 — live rectangle selection (draft/committed) for the slot count.
  const [selection, setSelection] = useState<GridSelection | null>(null);
  const toggleReveal = () => {
    setRevealZones((v) => !v);
    setZoneHoverRole(null);
  };
  // WO-VRD-10 — render EITHER the desktop top toolbar OR the mobile corner
  // HUD, never both, so testids (hud-zoom / grid-controls-*) stay unique in
  // the DOM (CSS `hidden` alone leaves duplicates that break e2e selectors).
  const isMdUp = useMediaQuery("(min-width: 768px)");
  // WO-VRD-5 — the horizontal→top-down showcase intro is disabled for
  // real users (per Claudio: removes the tilt + its load delay; the Grid
  // opens straight top-down). The tilt remains reachable ONLY via the
  // explicit `?showcase=on` debug/e2e flag, so the showcase regression
  // suite still exercises the gate. Interaction/lifecycle unchanged.
  const enableTilt =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("showcase") === "on";

  return (
    <div
      data-testid="grid-workspace"
      data-zone-overlay={zoneOverlay ? "on" : "off"}
      className={cn("relative w-full h-full min-w-0 min-h-0")}
    >
      {/* Layer 1 — Grid renderer (Phase 2A engine, external chrome).
          WO-P5 §SHOWCASE-INTRO — desktop only: on first visit the
          slab paints tilted (pseudo-isometric, presentation-only)
          and interaction is DISABLED until the tilt has resolved
          to top-down identity. Mobile skips the intro entirely
          (usability priority). Reduced-motion also skips. */}
      <ShowcaseIntro enableTilt={enableTilt}>
        <div
          data-testid="grid-base-layer"
          data-zone-overlay={zoneOverlay ? "on" : "off"}
          className="absolute inset-0 z-canvas"
        >
          <GridViewer
            ref={viewerRef}
            layout="fill"
            chrome="external"
            mode={mode}
            onModeChange={onModeChange}
            onCommitSelection={onCommitSelection}
            onClearSelection={onClearSelection}
            onContextStateChange={onContextStateChange}
            onHoverChange={setHoverWorld}
            onCameraChange={(s) => setZoomLevel(s.scale)}
            onStatusChange={setStatusLevel}
            onFirstReady={() => setParcelsReady(true)}
            zoneOverlay={zoneOverlay}
            revealZones={revealZones}
            revealHoverRole={zoneHoverRole}
            artworkOverlay={showDragArt}
            onSelectionChange={setSelection}
            onOccupancyChange={onOccupancyChange}
            refreshSignal={refreshSignal}
          />
        </div>
      </ShowcaseIntro>

      {/* Loading gate — covers the grid until parcels + zone overlay are ready,
          then fades out so everything reveals at once (WO — Claudio). */}
      <GridLoadingOverlay ready={gridReady} />

      {/* Layer 2..N — reserved for future ZoneOverlay / ParcelOverlay /
          ContentOverlay per WO#2 §18. Consumers may pass overlay nodes. */}
      <div
        aria-hidden={overlaySlot == null}
        data-testid="grid-overlay-slot"
        className="pointer-events-none absolute inset-0 z-overlay"
      >
        {overlaySlot}
      </div>

      {/* Layer 3 — HUD & Controls */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-hud"
      >
        {isMdUp ? (
          /* ───── DESKTOP — full instrument strip + full bottom toolbar (unchanged) ───── */
          <>
            <div
              data-testid="grid-instrument-slot"
              className={cn(
                "absolute pointer-events-auto z-hud",
                "top-4 left-1/2 -translate-x-1/2",
                "flex items-center w-max max-w-[calc(100%-1rem)]",
              )}
            >
              <CoordinateHUD
                variant="strip"
                hoverWorld={hoverWorld}
                zoomLevel={zoomLevel}
                leading={
                  <span className="inline-flex items-center gap-2">
                    <ContextModeToggle
                      mode={mode}
                      onChange={(m) => onModeChange?.(m)}
                      compact
                    />
                    {selection && <SlotCountBadge selection={selection} />}
                  </span>
                }
                trailing={
                  <span className="inline-flex items-center gap-2">
                    <GridControls
                      viewerRef={viewerRef}
                      orientation="horizontal"
                      size="sm"
                    />
                    <span
                      aria-hidden
                      className="h-3.5 w-px bg-border-divider shrink-0"
                    />
                    <MyTerritoriesButton />
                  </span>
                }
              />
            </div>
            <div
              data-testid="grid-bottom-toolbar"
              className={cn(
                "absolute pointer-events-auto z-hud",
                "bottom-4 left-1/2 -translate-x-1/2",
                "flex w-max max-w-[calc(100%-1rem)] px-2 max-lg:px-1.5 h-9 rounded-pill neon-glass",
              )}
            >
              {/* Glass stays on the outer pill: its backdrop-filter anchors the
                  Show & Drag message outside this scroll box (never clipped). */}
              <div
                data-toolbar-scroll
                onFocus={revealFocused}
                className={cn(
                  "flex items-center h-full gap-3 px-2 max-lg:gap-2 max-lg:px-1.5",
                  TOOLBAR_SCROLL,
                )}
              >
              <SectionOverline number="03" label={t("grid.toolbar.territory")} />
              <span aria-hidden className="h-4 w-px bg-border-divider" />
              <StatusBadge level={statusLevel} />
              <span aria-hidden className="h-4 w-px bg-border-divider" />
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 font-mono text-[10px] tracking-[0.12em] uppercase text-text-secondary select-none">
                <kbd className="font-mono text-[10px] px-1.5 py-px rounded-sm bg-elevated text-neon-teal border border-border-soft">
                  {IS_MAC ? "\u2318" : "Ctrl"}
                </kbd>
                {" + "}{t("grid.toolbar.scrollToZoom")}
              </span>
              <span aria-hidden className="h-4 w-px bg-border-divider" />
              <RevealAreasButton active={revealZones} onClick={toggleReveal} />
              <span aria-hidden className="h-4 w-px bg-border-divider" />
              <ShowDragArtworkButton active={showDragArt} onToggle={setShowDragArt} msgPlacement="up" />
              </div>
            </div>
            {zonesUnavailable ? (
              <ZoneUnavailableNotice
                className="absolute left-1/2 -translate-x-1/2 bottom-[52px] z-panel"
                onRetry={onRetryZones}
              />
            ) : revealZones && (
              <ZoneLegend
                className="absolute left-1/2 -translate-x-1/2 bottom-[52px] z-panel"
                activeRole={zoneHoverRole}
                onHoverRole={setZoneHoverRole}
              />
            )}
          </>
        ) : (
          /* ───── MOBILE — reduced bars (Claudio refinement):
              TOP: Select/Inspect · zoom +/−/center · My Territories.
              BOTTOM: LIVE · Reveal Areas · Show Artwork.
              Same components/functions as desktop, just the essential set.
              Hidden while the Territory sheet is open. ───── */
          !hideMobileChrome && (
            <>
              <div
                data-testid="grid-instrument-slot"
                className={cn(
                  "absolute pointer-events-auto z-hud",
                  "top-[calc(0.5rem+env(safe-area-inset-top,0px))] left-1/2 -translate-x-1/2",
                  "flex items-center gap-1",
                  "[&_button]:text-[9px] [&_button]:tracking-[0.1em] [&_button]:px-2",
                  "max-w-[calc(100vw-0.75rem)] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0",
                )}
              >
                <ContextModeToggle
                  mode={mode}
                  onChange={(m) => onModeChange?.(m)}
                  compact
                />
                <GridControls viewerRef={viewerRef} orientation="horizontal" size="sm" fitOnly />
                <MyTerritoriesButton />
              </div>
              <div
                data-testid="grid-bottom-toolbar"
                className={cn(
                  "absolute pointer-events-auto z-hud",
                  "bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] left-1/2 -translate-x-1/2",
                  "flex items-center gap-2",
                  "[&_button]:text-[9px] [&_button]:tracking-[0.1em] [&_button]:px-2",
                  "max-w-[calc(100vw-0.75rem)] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0",
                )}
              >
                <StatusBadge level={statusLevel} />
                <span aria-hidden className="h-4 w-px bg-border-divider" />
                <RevealAreasButton active={revealZones} onClick={toggleReveal} />
                <span aria-hidden className="h-4 w-px bg-border-divider" />
                <GridNavMenuButton />
              </div>
              {zonesUnavailable ? (
                <ZoneUnavailableNotice
                  className="absolute left-1/2 -translate-x-1/2 bottom-[calc(3.75rem+env(safe-area-inset-bottom,0px))] z-panel"
                  onRetry={onRetryZones}
                />
              ) : revealZones && (
                <ZoneLegend
                  className="absolute left-1/2 -translate-x-1/2 bottom-[calc(3.75rem+env(safe-area-inset-bottom,0px))] z-panel"
                  activeRole={zoneHoverRole}
                  onHoverRole={setZoneHoverRole}
                />
              )}
            </>
          )
        )}
      </div>

      {/* Macro #33 · W8 — zoom-discovery onboarding badge. Shown once the Grid
          is revealed; non-blocking overlay that never steals Grid gestures. */}
      {gridReady && (
        <ZoomDiscoveryBadge
          zoomSignal={zoomLevel}
          mobileStack={
            isMdUp || hideMobileChrome ? undefined : zonesUnavailable || revealZones ? "legend" : "toolbar"
          }
          desktopAboveLegend={isMdUp && (zonesUnavailable || revealZones)}
        />
      )}
    </div>
  );
}

/**
 * GridNavMenuButton — MACRO #31 PAGE 3 (Claudio). Mobile-only exit / main-nav
 * affordance, styled exactly like the other toolbar buttons (FACET pill). It
 * reveals the MobileBottomNav (via the `grid-nav-peek` <html> class) even while
 * the app navs are hidden in immersive mode, so the Grid is never a touch
 * dead-end. Lives inside the bottom Territory toolbar, where Show & Drag was.
 */
function GridNavMenuButton() {
  const { t } = useI18n();
  const [peek, setPeek] = useState(false);
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("grid-nav-peek", peek);
    return () => root.classList.remove("grid-nav-peek");
  }, [peek]);
  useEffect(() => {
    if (!peek) return;
    const close = () => setPeek(false);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, [peek]);
  return (
    <button
      type="button"
      data-testid="mobile-grid-nav-button"
      data-active={peek ? "true" : "false"}
      aria-pressed={peek}
      aria-label={peek ? t("grid.toolbar.hideNav") : t("grid.toolbar.openNav")}
      title={peek ? t("grid.toolbar.hideNav") : t("grid.toolbar.openNav")}
      onClick={() => setPeek((v) => !v)}
      className={FACET_CLASS}
      style={facetStyle(peek)}
    >
      <MenuGlyph />
      {t("grid.toolbar.menu")}
    </button>
  );
}

function MenuGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M2 3.5h10M2 7h10M2 10.5h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * StatusBadge — WO-VRD-9 territory sync pill for the top toolbar.
 * Mirrors the mobile in-canvas badge (LIVE / CACHED / CHAIN) with the
 * same truth source pushed from the viewer via `onStatusChange`.
 */
function StatusBadge({ level }: { level: "live" | "cached" | "chain" }) {
  const { t } = useI18n();
  const map = {
    live: { label: t("grid.status.live"), color: "#37e0c8", title: t("grid.status.liveTitle") },
    cached: {
      label: t("grid.status.cached"),
      color: "#ffb056",
      title: t("grid.status.cachedTitle"),
    },
    chain: {
      label: t("grid.status.chain"),
      color: "#ff5a6a",
      title: t("grid.status.chainTitle"),
    },
  } as const;
  const m = map[level];
  return (
    <span
      data-testid="territory-status-badge"
      data-level={level}
      title={m.title}
      className="inline-flex items-center gap-1.5 h-[22px] px-2.5 rounded-pill neon-glass font-mono text-[10px] font-semibold tracking-[0.14em] select-none"
      style={{ color: m.color, borderColor: `${m.color}55` }}
    >
      <span
        aria-hidden
        className="h-1.5 w-1.5 rounded-pill"
        style={{ background: m.color, boxShadow: `0 0 6px ${m.color}` }}
      />
      {m.label}
    </span>
  );
}


/**
 * RevealAreasButton — WO-VRD-33 pill toggle. Lights up (teal) while the
 * fluorescent zone overlay is active. Inline SVG glyph (no icon dep, to
 * match the codebase convention in ContextModeToggle / SideDrawer).
 */
function RevealAreasButton({
  active,
  onClick,
}: {
  active: boolean;
  onClick: () => void;
}) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      data-testid="reveal-zones-toggle"
      data-active={active ? "true" : "false"}
      aria-pressed={active}
      aria-label={t("grid.toolbar.revealAreasAria")}
      title={t("grid.toolbar.revealAreasAria")}
      onClick={onClick}
      className={FACET_CLASS}
      style={facetStyle(active)}
    >
      <LayersGlyph />
      {t("grid.toolbar.revealAreas")}
    </button>
  );
}

/**
 * ShowDragArtworkButton — enters "move artwork" mode: zooms the Grid out to
 * frame the owner's parcels + placed artwork, then opens the in-context
 * placement editor so the piece can be DRAGGED from one owned parcel to another
 * (backend stays the sole geometry/validity authority on Confirm).
 */
function ShowDragArtworkButton({
  active,
  onToggle,
  msgPlacement = "down",
}: {
  active: boolean;
  onToggle: (v: boolean) => void;
  msgPlacement?: "up" | "down";
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const flash = (m: string) => { setMsg(m); window.setTimeout(() => setMsg(null), 3500); };
  const handle = async () => {
    if (busy) return;
    if (active) {
      // Turn OFF — drop the overlay + any in-flight placement edit, AND clear
      // the deep-link params so the NEXT enable is always a real hash change
      // (re-setting an identical hash fires no `hashchange` → nothing happens).
      onToggle(false);
      placementEditStore.stop();
      window.dispatchEvent(new CustomEvent(ARTWORK_DESELECT_EVENT));
      if (window.location.hash.startsWith("#grid?")) window.location.hash = "#grid";
      return;
    }
    setBusy(true);
    try {
      const coll = await artworkApi.selfCollection();
      const ids = Array.from(new Set([...(coll.created_by ?? []), ...(coll.owned ?? [])].map((a) => a.artwork_id)));
      if (ids.length === 0) { flash(t("grid.artworkMsg.none")); return; }
      const arts = (await Promise.all(ids.map((id) => artworkApi.getArtwork(id).catch(() => null))))
        .filter((a): a is NonNullable<typeof a> => !!a && !!a.placement);
      if (arts.length === 0) { flash(t("grid.artworkMsg.noPlaced")); return; }
      const target = arts[0];
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const a of arts) {
        const p = a.placement!;
        minX = Math.min(minX, p.anchor_x); minY = Math.min(minY, p.anchor_y);
        maxX = Math.max(maxX, p.anchor_x + a.width); maxY = Math.max(maxY, p.anchor_y + a.height);
      }
      try {
        const res = await fetch(`/api/v1/parcels?owner=${target.owner}&limit=500`, { credentials: "include" });
        const pj = await res.json();
        for (const pc of (pj.parcels ?? [])) {
          minX = Math.min(minX, pc.x); minY = Math.min(minY, pc.y);
          maxX = Math.max(maxX, pc.x + pc.w); maxY = Math.max(maxY, pc.y + pc.h);
        }
      } catch { /* parcels are advisory for framing only */ }
      const cx = Math.round((minX + maxX) / 2);
      const cy = Math.round((minY + maxY) / 2);
      const span = Math.max(60, Math.round(Math.max(maxX - minX, maxY - minY) * 1.6));
      // ov=1 → panoramic zoom-out; the artwork overlay stays mounted + grabbable.
      // `t` nonce guarantees a real hash change even on repeated enables.
      onToggle(true);
      window.location.hash = `#grid?fx=${cx}&fy=${cy}&aid=${target.artwork_id}&edit=1&span=${span}&ov=1&t=${Date.now()}`;
    } catch {
      flash(t("grid.artworkMsg.connectWallet"));
    } finally {
      setBusy(false);
    }
  };
  // Not positioned: the message anchors to the toolbar so its scroll box never clips it.
  return (
    <div>
      <button
        type="button"
        data-testid="show-drag-artwork-btn"
        data-active={active ? "true" : "false"}
        aria-pressed={active}
        aria-label={t("grid.toolbar.showDragArtworkAria")}
        title={t("grid.toolbar.showDragArtworkTitle")}
        onClick={handle}
        disabled={busy}
        className={cn(FACET_CLASS, "disabled:opacity-50")}
        style={facetStyle(active)}
      >
        <DragGlyph />
        {busy ? t("grid.toolbar.loading") : t("grid.toolbar.showDragArtwork")}
      </button>
      {msg && (
        <span data-testid="show-drag-artwork-msg"
          className={cn(
            "absolute right-0 z-panel whitespace-nowrap rounded-md border border-white/15 bg-black/85 px-2.5 py-1 text-[11px] text-white/80 shadow-lg",
            msgPlacement === "up" ? "bottom-full mb-2" : "top-full mt-2",
          )}>
          {msg}
        </span>
      )}
    </div>
  );
}

function DragGlyph() {
  return (
    <svg viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden>
      <path d="M7 1v12M1 7h12" strokeLinecap="round" opacity="0.5" />
      <rect x="4.5" y="4.5" width="5" height="5" rx="1" />
    </svg>
  );
}


function LayersGlyph() {
  return (
    <svg
      viewBox="0 0 14 14"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinejoin="round"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M7 1.5 12.5 4.3 7 7.1 1.5 4.3 7 1.5Z" />
      <path d="M1.5 7 7 9.8 12.5 7" />
      <path d="M1.5 9.7 7 12.5 12.5 9.7" />
    </svg>
  );
}

/**
 * SlotCountBadge — WO-VRD-34. Live readout of how many slots the current
 * rectangle selection covers (w × h). Sits right after the Select toggle
 * (the "crosshair") in the Instrument strip while a selection is active.
 */
function SlotCountBadge({ selection }: { selection: GridSelection }) {
  const { t } = useI18n();
  const slots = selection.w * selection.h;
  return (
    <span
      data-testid="selection-slot-count"
      data-slots={slots}
      title={t("grid.slotCount.selectedTitle", { slots: slots.toLocaleString(), w: selection.w, h: selection.h })}
      className={cn(
        "relative inline-flex items-center gap-1.5 px-2 py-1 shrink-0",
        "font-mono text-[10px] uppercase tracking-[0.14em] select-none",
      )}
      style={{
        clipPath:
          "polygon(6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px), 0 6px)",
        background:
          "linear-gradient(180deg, rgba(45, 182, 240,0.18) 0%, rgba(45, 182, 240,0.08) 100%)",
        border: "1px solid rgba(45, 182, 240,0.55)",
        color: "var(--spec-cyan)",
      }}
    >
      <span className="tabular-nums text-text-primary">
        {slots.toLocaleString()}
      </span>
      <span>{t("grid.slotCount.slots")}</span>
    </span>
  );
}


/**
 * MyTerritoriesButton — quick-nav pill in the Instrument bar (user request).
 * Jumps to the owner Dashboard (#dashboard = "My Territories"). Styled to
 * match the aerospace instrument aesthetic (neon-glass pill).
 */
function MyTerritoriesButton() {
  const { t } = useI18n();
  return (
    <a
      data-testid="instrument-my-territories"
      href="#dashboard"
      title={t("grid.toolbar.myTerritories")}
      aria-label={t("grid.toolbar.myTerritories")}
      className={cn(FACET_CLASS, "no-underline")}
      style={facetStyle(false)}
    >
      <TerritoriesGlyph />
      {t("grid.toolbar.myTerritories")}
    </a>
  );
}

function TerritoriesGlyph() {
  return (
    <svg
      viewBox="0 0 14 14"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
      strokeLinejoin="round"
      strokeLinecap="round"
      aria-hidden
    >
      <rect x="1.5" y="1.5" width="4.5" height="4.5" rx="0.6" />
      <rect x="8" y="1.5" width="4.5" height="4.5" rx="0.6" />
      <rect x="1.5" y="8" width="4.5" height="4.5" rx="0.6" />
      <rect x="8" y="8" width="4.5" height="4.5" rx="0.6" />
    </svg>
  );
}
